import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Loader, CheckCircle2 } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ScheduleACall() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="37402" id="get-in-touch-form" ref={ref} className={`w-full bg-background py-24 px-4 md:px-8 transition-opacity duration-700 ${isVisible ? "opacity-100" : "opacity-0"}`}>
      <div className="max-w-[800px] mx-auto">
        <div className="text-center mb-16">
          <h2 className="font-sans font-bold text-4xl md:text-5xl text-foreground mb-4 tracking-tight">
            Get in Touch
          </h2>
          <p className="text-muted-foreground font-sans text-base md:text-lg max-w-lg mx-auto">
            Improve efficiency and keep your solar system performing at its best with a professional clean.
          </p>
        </div>

        <FormProvider formKey="contact_form_contact_us" formId="contact_form_contact_us" sectionName="Schedule Call Section" defaultValues={{
        full_name: "",
        email: "",
        phone: "",
        company: "",
        message: ""
      }} validationRules={{
        full_name: {
          required: "Full Name is required"
        },
        email: {
          required: "Email Address is required",
          pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: "Please enter a valid email address"
          }
        },
        phone: {
          required: "Phone Number is required"
        },
        company: {
          required: false
        },
        message: {
          required: false
        }
      }} submitText="Send Request" successMessage="Your request has been received. Our team will contact you shortly to confirm.">
          <ScheduleForm />
        </FormProvider>
      </div>
    </section>;
}
function ScheduleForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control,
    resetForm
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="w-full p-10 border border-border bg-card text-center animate-in fade-in zoom-in duration-500 rounded-3xl shadow-xl shadow-primary/5">
        <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-primary/10 mb-6">
          <CheckCircle2 className="w-10 h-10 text-primary" />
        </div>
        <h3 className="font-sans font-bold text-2xl text-foreground mb-2">Request Sent</h3>
        <p className="text-muted-foreground font-sans mb-8">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="border-primary text-primary hover:bg-primary hover:text-primary-foreground rounded-full px-10 transition-all duration-300">
          Send Another Request
        </Button>
      </div>;
  }
  const inputStyles = "bg-white/50 border border-input rounded-xl px-4 py-6 text-foreground placeholder:text-muted-foreground/60 focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary transition-all duration-300 shadow-sm";
  const labelStyles = "uppercase text-[12px] tracking-wider text-muted-foreground font-sans font-bold";
  return <Form onSubmit={handleSubmit} className="space-y-10">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
        <FormField control={control} name="full_name" render={({
        field
      }) => <FormItem className="space-y-2">
              <FormLabel className={labelStyles}>Full Name</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" className={inputStyles} {...field} />
              </FormControl>
              <FormMessage className="text-destructive text-xs" />
            </FormItem>} />

        <FormField control={control} name="email" render={({
        field
      }) => <FormItem className="space-y-2">
              <FormLabel className={labelStyles}>Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@powershine.com.au" className={inputStyles} {...field} />
              </FormControl>
              <FormMessage className="text-destructive text-xs" />
            </FormItem>} />

        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem className="space-y-2">
              <FormLabel className={labelStyles}>Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="0466 880 255" className={inputStyles} {...field} />
              </FormControl>
              <FormMessage className="text-destructive text-xs" />
            </FormItem>} />

        <FormField control={control} name="company" render={({
        field
      }) => <FormItem className="space-y-2">
              <FormLabel className={labelStyles}>Property Type (Optional)</FormLabel>
              <FormControl>
                <Input placeholder="Residential or Commercial" className={inputStyles} {...field} />
              </FormControl>
              <FormMessage className="text-destructive text-xs" />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem className="space-y-2">
            <FormLabel className={labelStyles}>Additional Details</FormLabel>
            <FormControl>
              <Textarea placeholder="Optional: Tell us roughly how many panels you have and whether the property is single or double storey. For commercial properties, please mention if there is safe roof access or anchor points." className={`${inputStyles} min-h-[120px] resize-none`} {...field} />
            </FormControl>
            <FormMessage className="text-destructive text-xs" />
          </FormItem>} />

      {submitError && <div className="p-4 border border-destructive/20 bg-destructive/5 text-destructive text-sm text-center rounded-xl">
          {submitError}
        </div>}

      <div className="pt-6">
        <Button type="submit" disabled={isSubmitting} className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-bold text-base py-8 rounded-full transition-all duration-300 shadow-xl shadow-primary/20 hover:scale-[1.01] active:scale-[0.99]">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 h-5 w-5" />
              SENDING...
            </> : "SEND REQUEST"}
        </Button>
      </div>
    </Form>;
}